import {useCallback, useEffect, useRef, useState} from 'react'
import DraftPersistentService from '../../persistence/DraftPersistentService'
import DraftPreviewStore from '../../persistence/DraftPreviewStore'
import DraftImageStore from '../../persistence/DraftImageStore'

/**
 * Hook to load all draft entries from localStorage and their preview Blobs from IndexedDB.
 * Returns { drafts, loading, deleteDraft } where drafts is array of { id, document, previewUrl, updatedAt }.
 * Handles object URL lifecycle (revoke on unmount or when list changes).
 */
export function useDraftShapes() {
    const [drafts, setDrafts] = useState([])
    const [loading, setLoading] = useState(true)
    const urlsRef = useRef([])

    useEffect(() => {
        let cancelled = false

        async function loadDrafts() {
            setLoading(true)
            const entries = DraftPersistentService.getAll()

            const draftsWithUrls = await Promise.all(
                entries.map(async (entry) => {
                    let previewUrl = null
                    if (entry.previewKey) {
                        try {
                            const record = await DraftPreviewStore.load(entry.previewKey)
                            if (record?.blob) {
                                previewUrl = URL.createObjectURL(record.blob)
                            }
                        } catch {
                            // IndexedDB unavailable or record missing
                        }
                    }
                    return {
                        id: entry.id,
                        document: entry.document,
                        previewUrl,
                        updatedAt: entry.updatedAt,
                    }
                }),
            )

            if (cancelled) {
                draftsWithUrls.forEach((d) => d.previewUrl && URL.revokeObjectURL(d.previewUrl))
                return
            }

            urlsRef.current.forEach((url) => URL.revokeObjectURL(url))
            urlsRef.current = draftsWithUrls.map((d) => d.previewUrl).filter(Boolean)

            setDrafts(draftsWithUrls)
            setLoading(false)
        }

        loadDrafts()

        return () => {
            cancelled = true
            urlsRef.current.forEach((url) => URL.revokeObjectURL(url))
            urlsRef.current = []
        }
    }, [])

    const deleteDraft = useCallback((id) => {
        const entry = DraftPersistentService.loadOne(id)
        setDrafts((prev) => {
            const draft = prev.find((d) => d.id === id)
            if (draft?.previewUrl) {
                URL.revokeObjectURL(draft.previewUrl)
                urlsRef.current = urlsRef.current.filter((url) => url !== draft.previewUrl)
            }
            return prev.filter((d) => d.id !== id)
        })
        ;(async () => {
            try {
                if (entry?.previewKey) {
                    await DraftPreviewStore.delete(entry.previewKey)
                }
                await DraftImageStore.deleteByDraftId(id)
                DraftPersistentService.deleteOne(id)
            } catch {
                // Cleanup is best-effort; don't block the main flow.
            }
        })()
    }, [])

    return {drafts, loading, deleteDraft}
}
